import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { addMonths, format, isSameDay, isSameMonth, startOfMonth, startOfWeek, addDays } from "date-fns"

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

function buildWeeks(monthDate) {
  const start = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 })
  const days = Array.from({ length: 42 }, (_, i) => addDays(start, i))
  const weeks = []
  for (let i = 0; i < 42; i += 7) weeks.push(days.slice(i, i + 7))
  return weeks
}

function Calendar({
  selected,
  onSelect,
  disabled,
  fromDate,
}) {
  const initial = selected instanceof Date ? selected : new Date()
  const [month, setMonth] = React.useState(() => startOfMonth(initial))
  const weeks = buildWeeks(month)

  const isDisabled = (date) => {
    if (typeof disabled === "function") return disabled(date)
    if (fromDate) {
      const a = new Date(date)
      const b = new Date(fromDate)
      a.setHours(0, 0, 0, 0)
      b.setHours(0, 0, 0, 0)
      return a < b
    }
    return false
  }

  return (
    <div
      style={{
        width: 320,
        padding: "16px 16px 12px",
        background: "#ffffff",
        color: "#0f3e17",
        fontFamily: '"Instrument Sans", system-ui, sans-serif',
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => setMonth((m) => addMonths(m, -1))}
          style={navStyle}
        >
          <ChevronLeft size={16} />
        </button>
        <p
          style={{
            margin: 0,
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "#0f3e17",
          }}
        >
          {format(month, "MMMM yyyy")}
        </p>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => setMonth((m) => addMonths(m, 1))}
          style={navStyle}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
        }}
      >
        <thead>
          <tr>
            {WEEKDAYS.map((day) => (
              <th
                key={day}
                style={{
                  width: "14.2857%",
                  height: 32,
                  padding: 0,
                  margin: 0,
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#3d4a42",
                  textAlign: "center",
                  verticalAlign: "middle",
                }}
              >
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0].toISOString()}>
              {week.map((date) => {
                const outside = !isSameMonth(date, month)
                const selectedDay = selected instanceof Date && isSameDay(date, selected)
                const today = isSameDay(date, new Date())
                const blocked = isDisabled(date)
                return (
                  <td
                    key={date.toISOString()}
                    style={{
                      width: "14.2857%",
                      height: 40,
                      padding: 0,
                      margin: 0,
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    <button
                      type="button"
                      disabled={blocked}
                      aria-label={format(date, "EEEE, d MMMM yyyy")}
                      aria-selected={selectedDay}
                      onClick={() => {
                        if (blocked) return
                        onSelect?.(date)
                      }}
                      style={{
                        width: 36,
                        height: 36,
                        margin: "0 auto",
                        border: today && !selectedDay ? "1px solid #0f3e17" : "0",
                        borderRadius: 999,
                        background: selectedDay ? "#0f3e17" : "transparent",
                        color: blocked ? "#c5cbc6" : selectedDay ? "#fffefc" : outside ? "#9aa39c" : "#0f3e17",
                        fontSize: 14,
                        fontFamily: "inherit",
                        lineHeight: "36px",
                        padding: 0,
                        cursor: blocked ? "default" : "pointer",
                        display: "block",
                      }}
                    >
                      {date.getDate()}
                    </button>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const navStyle = {
  width: 32,
  height: 32,
  border: "1px solid #E8ECF2",
  background: "#fffefc",
  borderRadius: 999,
  color: "#0f3e17",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  padding: 0,
}

Calendar.displayName = "Calendar"

export { Calendar }
